// CADUpload - Upload CAD files to create a digital twin
import { useState, useCallback, useRef } from 'react'
import { useTranslation } from '../hooks/useTranslation'
import { useMutation } from '@tanstack/react-query'
import {
  CloudArrowUpIcon,
  DocumentIcon,
  XMarkIcon,
  CheckCircleIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'
import { twinApi, type TwinProject } from '../lib/api'

interface CADUploadProps {
  assetName: string
  onProjectCreated?: (project: TwinProject) => void
  onError?: (error: string) => void
  className?: string
}

type UploadState = 'idle' | 'creating' | 'uploading' | 'processing' | 'complete' | 'error'

const ACCEPTED_FORMATS = [
  '.step', '.stp',
  '.iges', '.igs',
  '.stl',
  '.obj',
  '.gltf', '.glb',
]

export function CADUpload({ assetName, onProjectCreated, onError, className = '' }: CADUploadProps) {
  const { t } = useTranslation()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [uploadState, setUploadState] = useState<UploadState>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [progress, setProgress] = useState(0)
  const [createdProject, setCreatedProject] = useState<TwinProject | null>(null)

  const createProjectMutation = useMutation({
    mutationFn: (name: string) => twinApi.createProject({ name, metadata: { source: 'asset-upload' } }),
    onSuccess: (project) => {
      setCreatedProject(project)
    },
  })

  const handleFileSelect = useCallback((file: File) => {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase()
    if (!ACCEPTED_FORMATS.includes(extension)) {
      setErrorMessage(`Unsupported file format. Accepted: ${ACCEPTED_FORMATS.join(', ')}`)
      return
    }
    setSelectedFile(file)
    setErrorMessage(null)
    setUploadState('idle')
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files[0]
    if (file) handleFileSelect(file)
  }, [handleFileSelect])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
  }, [])

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFileSelect(file)
  }, [handleFileSelect])

  const clearFile = useCallback(() => {
    setSelectedFile(null)
    setUploadState('idle')
    setErrorMessage(null)
    setProgress(0)
    setCreatedProject(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }, [])

  const uploadFile = useCallback(async () => {
    if (!selectedFile) return

    try {
      // Step 1: Create Twin project
      setUploadState('creating')
      setProgress(10)

      const projectName = `${assetName} - Twin`
      const project = await createProjectMutation.mutateAsync(projectName)

      // Step 2: Upload CAD file
      setUploadState('uploading')
      setProgress(30)

      await twinApi.uploadCAD(project.id, selectedFile)

      // Step 3: Monitor conversion
      setUploadState('processing')
      setProgress(60)

      // Poll for conversion status
      let attempts = 0
      const maxAttempts = 60 // 5 minutes max
      const pollInterval = 5000 // 5 seconds

      const checkStatus = async (): Promise<TwinProject> => {
        const status = await twinApi.getConversionStatus(project.id)

        if (status.status === 'completed') {
          setProgress(100)
          const updatedProject = await twinApi.getProject(project.id)
          return updatedProject
        }

        if (status.status === 'failed') {
          throw new Error(status.error || 'Conversion failed')
        }

        attempts++
        if (attempts >= maxAttempts) {
          throw new Error('Conversion timed out')
        }

        // Update progress based on conversion progress
        if (status.progress) {
          setProgress(60 + Math.floor(status.progress * 0.4))
        }

        await new Promise(resolve => setTimeout(resolve, pollInterval))
        return checkStatus()
      }

      const finalProject = await checkStatus()

      setUploadState('complete')
      setCreatedProject(finalProject)
      onProjectCreated?.(finalProject)

    } catch (error) {
      console.error('[CADUpload] Error:', error)
      setUploadState('error')
      const message = error instanceof Error ? error.message : 'Upload failed'
      setErrorMessage(message)
      onError?.(message)
    }
  }, [selectedFile, assetName, createProjectMutation, onProjectCreated, onError])

  return (
    <div className={`${className}`}>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {t('twin.upload.title')} <span className="text-gray-400 font-normal">({t('common.optional')})</span>
      </label>

      {/* Drop zone */}
      {!selectedFile && (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer hover:border-brand-400 hover:bg-gray-50 transition-colors"
        >
          <CloudArrowUpIcon className="mx-auto h-10 w-10 text-gray-400" />
          <p className="mt-2 text-sm text-gray-600">
            {t('twin.upload.dropzone')}
          </p>
          <p className="mt-1 text-xs text-gray-500">
            {t('twin.upload.formats')}
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept={ACCEPTED_FORMATS.join(',')}
            onChange={handleFileInputChange}
            className="hidden"
          />
        </div>
      )}

      {/* Selected file */}
      {selectedFile && (
        <div className="border border-gray-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <DocumentIcon className="h-6 w-6 text-gray-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                <p className="text-xs text-gray-500">
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </p>
              </div>
            </div>
            {uploadState === 'idle' && (
              <button
                type="button"
                onClick={clearFile}
                className="p-1 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Progress bar */}
          {(uploadState === 'creating' || uploadState === 'uploading' || uploadState === 'processing') && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                <span>
                  {uploadState === 'creating' && t('twin.upload.creatingProject')}
                  {uploadState === 'uploading' && t('twin.upload.uploading')}
                  {uploadState === 'processing' && t('twin.upload.converting')}
                </span>
                <span>{progress}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-brand-500 transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {/* Complete state */}
          {uploadState === 'complete' && (
            <div className="mt-4 flex items-center gap-2 text-green-600">
              <CheckCircleIcon className="h-5 w-5" />
              <span className="text-sm font-medium">{t('twin.upload.complete')}</span>
            </div>
          )}

          {/* Error state */}
          {uploadState === 'error' && (
            <div className="mt-4 flex items-center gap-2 text-red-600">
              <ExclamationTriangleIcon className="h-5 w-5" />
              <span className="text-sm">{errorMessage}</span>
            </div>
          )}

          {/* Action buttons */}
          <div className="mt-4 flex gap-2">
            {uploadState === 'idle' && (
              <>
                <button
                  type="button"
                  onClick={uploadFile}
                  className="flex-1 inline-flex justify-center items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
                >
                  <CloudArrowUpIcon className="h-4 w-4" />
                  {t('twin.upload.uploadAndConvert')}
                </button>
                <button
                  type="button"
                  onClick={clearFile}
                  className="px-4 py-2 text-gray-600 text-sm font-medium hover:text-gray-800 transition-colors"
                >
                  {t('common.cancel')}
                </button>
              </>
            )}
            {uploadState === 'error' && (
              <>
                <button
                  type="button"
                  onClick={uploadFile}
                  className="flex-1 inline-flex justify-center items-center gap-2 px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700 transition-colors"
                >
                  <ArrowPathIcon className="h-4 w-4" />
                  {t('twin.upload.retry')}
                </button>
                <button
                  type="button"
                  onClick={clearFile}
                  className="px-4 py-2 text-gray-600 text-sm font-medium hover:text-gray-800 transition-colors"
                >
                  {t('common.clear')}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Error message (for format errors) */}
      {errorMessage && uploadState === 'idle' && (
        <p className="mt-2 text-sm text-red-600">{errorMessage}</p>
      )}

      {/* Created project info */}
      {createdProject && uploadState === 'complete' && (
        <input
          type="hidden"
          name="twinProjectId"
          value={createdProject.id}
        />
      )}
    </div>
  )
}

// Export project ID getter for form submission
export function useCADUploadProjectId(uploadRef: React.RefObject<{ projectId: string | null }>) {
  return uploadRef.current?.projectId ?? null
}
